import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { Chip, Typography } from '@mui/material';

import { PostStatus } from '@libs/types';
import { DataGridLink, RenewPostInput } from '@carcareers/components';
import { routes } from '@carcareers/utils';

export const DEFAULT_POST_DURATION = 30;

export const DEFAULT_POST = {
  job: 0,
  rooftop: 0,
  status: PostStatus.PENDING,
  startDate: new Date(),
  endDate: new Date().setDate(new Date().getDate() + DEFAULT_POST_DURATION),
};

export const POST_INDEX_COLUMNS: GridColDef[] = [
  { field: 'id', headerName: 'ID', width: 70 },
  {
    field: 'idLink',
    headerName: 'View',
    width: 90,
    renderCell: (params) => (
      <DataGridLink
        link={`/${routes.EMPLOYER_HOME}/${routes.EMPLOYER_POSTS_VIEW.replace(':id', params.row.id.toString())}`}
        content={params.row.id}
      />
    ),
  },
  {
    field: 'job',
    headerName: 'Job',
    width: 150,
    renderCell: (params) => {
      const path = `/${routes.EMPLOYER_HOME}/${routes.EMPLOYER_POSTS_VIEW.replace(':id', params.row.id.toString())}`;
      return <DataGridLink link={path} content={params.row.job.title} />;
    },
  },
  {
    field: 'rooftop',
    headerName: 'Roof Top',
    width: 150,
    renderCell: (params) => {
      const rowId = params.row.job.rooftop.id.toString();
      const path = `/${routes.EMPLOYER_HOME}/${routes.EMPLOYER_ROOFTOPS_VIEW.replace(':id', rowId)}`;
      return <DataGridLink link={path} content={params.row.job.rooftop.name} />;
    },
  },
  {
    field: 'location',
    headerName: 'Address',
    width: 150,
    valueGetter: (_, row) => row.job.rooftop.location,
  },
  {
    field: 'startDate',
    headerName: 'Start Date',
    width: 150,
    valueGetter: (_, row) => {
      const date = new Date(row.startDate);
      date.setMinutes(date.getMinutes() + date.getTimezoneOffset());
      return date.toLocaleString('en-US', { month: 'short', day: '2-digit', year: 'numeric' });
    },
  },
  {
    field: 'endDate',
    headerName: 'End Date',
    width: 150,
    valueGetter: (_, row) => {
      const date = new Date(row.endDate);
      date.setMinutes(date.getMinutes() + date.getTimezoneOffset());
      return date.toLocaleString('en-US', { month: 'short', day: '2-digit', year: 'numeric' });
    },
  },
  {
    field: 'postedBy',
    headerName: 'Posted By',
    width: 150,
    renderCell: (params) => {
      return <Typography>{params.row.postedBy.user?.email}</Typography>;
    },
  },
  {
    field: 'status',
    headerName: 'Status',
    width: 100,
    renderCell: (params) => {
      switch (params.row.status) {
        case 'active':
          return <Chip label={params.row.status} color="success" />;
        case 'expired':
          return <Chip label={params.row.status} color="warning" />;
        case 'closed':
          return <Chip label={params.row.status} color="secondary" />;
        default:
          return <Chip label={params.row.status} />;
      }
    },
  },
  {
    field: 'applicationCount',
    headerName: 'Applications',
    width: 100,
    renderCell: (params) => {
      const path = `/${routes.EMPLOYER_HOME}/${routes.EMPLOYER_POSTS_VIEW.replace(':id', params.row.id.toString())}`;
      return <DataGridLink link={path} content={params.row.applicationCount ?? 0} />;
    },
  },
  {
    field: 'renew',
    headerName: 'Auto Renew',
    width: 100,
    renderCell: (params: GridRenderCellParams) => <RenewPostInput post={params.row} />,
  },
  {
    field: 'updatedAt',
    headerName: 'Updated',
    width: 150,
    valueGetter: (_, row) =>
      new Date(row.updatedAt).toLocaleString('en-US', {
        month: 'short',
        day: '2-digit',
        year: 'numeric',
      }),
  },
  {
    field: 'createdAt',
    headerName: 'Created',
    width: 150,
    valueGetter: (_, row) =>
      new Date(row.createdAt).toLocaleString('en-US', {
        month: 'short',
        day: '2-digit',
        year: 'numeric',
      }),
  },
];

export const POST_INDEX_COLUMN_INITIAL_STATE = {
  id: true,
  job: true,
  rooftop: true,
  location: true,
  startDate: false,
  endDate: true,
  spend: false,
  postedBy: false,
  status: true,
  applicationCount: true,
  renew: true,
  updatedAt: false,
  createdAt: false,
};

export const ADMIN_POST_INDEX_COLUMNS: GridColDef[] = [
  { field: 'id', headerName: 'ID', width: 90 },
  {
    field: 'job',
    headerName: 'Job',
    width: 150,
    renderCell: (params) => {
      const rowId = params.row.id.toString();
      const path = `/${routes.ADMIN_HOME}/${routes.ADMIN_POSTS_VIEW.replace(':id', rowId)}`;
      return <DataGridLink link={path} content={params.row.job.title} />;
    },
  },
  {
    field: 'rooftop',
    headerName: 'Roof Top',
    width: 150,
    renderCell: (params) => {
      const rowId = params.row.job.rooftop.id.toString();
      const path = `/${routes.ADMIN_HOME}/${routes.ADMIN_ROOFTOPS_VIEW.replace(':id', rowId)}`;
      return <DataGridLink link={path} content={params.row.job.rooftop.name} />;
    },
  },
  {
    field: 'location',
    headerName: 'Location',
    width: 150,
    valueGetter: (_, row) => row.job.rooftop.location,
  },
  {
    field: 'startDate',
    headerName: 'Start',
    width: 150,
    valueGetter: (_, row) => {
      const date = new Date(row.startDate);
      date.setMinutes(date.getMinutes() + date.getTimezoneOffset());
      return date.toLocaleString('en-US', { month: 'short', day: '2-digit' });
    },
  },
  {
    field: 'endDate',
    headerName: 'End',
    width: 150,
    valueGetter: (_, row) => {
      const date = new Date(row.endDate);
      date.setMinutes(date.getMinutes() + date.getTimezoneOffset());
      return date.toLocaleString('en-US', { month: 'short', day: '2-digit' });
    },
  },
  {
    field: 'spend',
    headerName: 'Spend',
    width: 150,
    valueGetter: (_, row) => `$${Number(row.invoice?.totalPaymentDue ?? 0).toFixed(2)}`,
  },
  {
    field: 'postedBy',
    headerName: 'Posted By',
    width: 150,
    renderCell: (params) => {
      return <Typography>{params.row.postedBy.user?.email}</Typography>;
    },
  },
  {
    field: 'status',
    headerName: 'Status',
    width: 150,
    renderCell: (params) => {
      switch (params.row.status) {
        case 'active':
          return <Chip label={params.row.status} color="success" />;
        case 'expired':
          return <Chip label={params.row.status} color="warning" />;
        case 'closed':
          return <Chip label={params.row.status} color="secondary" />;
        default:
          return <Chip label={params.row.status} />;
      }
    },
  },
  {
    field: 'applicationCount',
    headerName: 'Applies',
    width: 150,
    renderCell: (params) => {
      const rowId = params.row.id.toString();
      const path = `/${routes.ADMIN_HOME}/${routes.ADMIN_POSTS_VIEW.replace(':id', rowId)}`;
      return <DataGridLink link={path} content={params.row.applicationCount ?? 0} />;
    },
  },
  {
    field: 'renew',
    headerName: 'Auto Renew',
    width: 150,
    renderCell: (params: GridRenderCellParams) => <RenewPostInput post={params.row} />,
  },
  {
    field: 'updatedAt',
    headerName: 'Updated',
    width: 150,
    valueGetter: (_, row) =>
      new Date(row.updatedAt).toLocaleString('en-US', {
        month: 'short',
        day: '2-digit',
        year: 'numeric',
      }),
  },
  {
    field: 'createdAt',
    headerName: 'Created',
    width: 150,
    valueGetter: (_, row) =>
      new Date(row.createdAt).toLocaleString('en-US', {
        month: 'short',
        day: '2-digit',
        year: 'numeric',
      }),
  },
];

export const ADMIN_POST_INDEX_COLUMN_INITIAL_STATE = {
  id: true,
  job: true,
  rooftop: true,
  location: true,
  startDate: true,
  endDate: true,
  spend: false,
  postedBy: false,
  status: true,
  applicationCount: true,
  renew: false,
  updatedAt: false,
  createdAt: false,
};
